/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';

type CustomDatePickerProps = {
  value: any;
  onChange: (value: string) => void;
  label?: string;
  format?: string;
  min?: string;
  max?: string;
  minDate?: number;
  maxDate?: number;
  minMonth?: number;
  maxMonth?: number;
  minYear?: number;
  maxYear?: number;
  type?: 'general' | 'firstPayDate' | 'secondPayDate' | 'dateOfBirth';
  required?: boolean;
  isErrorPresent?: any;
  firstPayDate?: string;
  secondPayDate?: string;
  disabled?: any;
};

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  value,
  onChange,
  label = 'Date',
  format = 'MM/DD/YYYY',
  min,
  max,
  minDate,
  maxDate,
  minMonth,
  maxMonth,
  minYear,
  maxYear,
  type = 'general',
  required = false,
  isErrorPresent,
  firstPayDate,
  secondPayDate,
  disabled = false
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(value ? dayjs(value, 'MM/DD/YYYY') : null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const containerRef = useRef<any>(null);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (containerRef.current && !containerRef.current?.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      const parsed = dayjs(value, 'MM/DD/YYYY');
      if (parsed.isValid()) {
        setSelectedDate(parsed);
        setCurrentMonth(parsed);
      }
    } else if (type === 'secondPayDate' && min) {
      const minDate = dayjs(min).startOf('month');
      setCurrentMonth(minDate);
    } else if (type === 'dateOfBirth') {
      const dobDefault = dayjs().subtract(18, 'year').startOf('month');
      setCurrentMonth(dobDefault);
    } else {
      setCurrentMonth(dayjs());
    }
    setInputValue(value || '');
  }, [value, min, type]);

  useEffect(() => {
    if (value) {
      const parsed = dayjs(value, 'YYYY-MM-DD');
      if (parsed.isValid()) {
        setSelectedDate(parsed);
        setCurrentMonth(parsed);
        if (type === 'dateOfBirth') {
          setInputValue(formatDisplayForDOB(value));
        } else {
          setInputValue(parsed.format('MM/DD/YYYY'));
        }
      } else {
        setInputValue('');
      }
    } else {
      setInputValue('');
    }
  }, [value, type]);

  useEffect(() => {
    if(type == 'general') setError('')
  }, [type])

  useEffect(() => {
    if (type !== 'firstPayDate' && type !== 'secondPayDate') return;
    const { valid, message } = validatePayDates();
    if (!valid) {
      setError(message || '');
      isErrorPresent?.(true);
    } else {
      setError('');
      isErrorPresent?.(false);
    }
  }, [firstPayDate, secondPayDate, type]);

    const validatePayDates = () => {
        if (!firstPayDate || !secondPayDate) return { valid: true };

        const first = dayjs(firstPayDate, 'MM/DD/YYYY');
        const second = dayjs(secondPayDate, 'MM/DD/YYYY');
        const now = dayjs().startOf('month');

        if (type === 'firstPayDate') {
            if (first.isAfter(second)) {
                return { valid: false, message: 'First Pay Date should not be after Second Pay Date' };
            }
            if (first.isBefore(now)) {
                return { valid: false, message: 'First Pay Date must be in the current month or later' };
            }
        }

        if (type === 'secondPayDate') {
            if (second.isBefore(first)) {
                return { valid: false, message: 'Second Pay Date should not be before First Pay Date' };
            }
            if (second.isBefore(now)) {
                return { valid: false, message: 'Second Pay Date must be in the current month or later' };
            }
        }

        return { valid: true };
    };

  const formatDisplayForDOB = (dateStr: string) => {
    if (!dateStr) return '';
    const parsed = dayjs(dateStr, 'YYYY-MM-DD');
    if (!parsed.isValid()) return '';
    const month = parsed.format('MM');
    const day = parsed.format('DD');
    return `${month}/${day}/****`;
  };

  const isDateDisabled = (date: any) => {
    const d = dayjs(date);
    if (!d.isValid()) return true;

    if (type === 'secondPayDate' && min) {
      const minDate = dayjs(min).startOf('month');
      if (d.isBefore(minDate)) return true;
    }

    if (type === 'dateOfBirth') {
      const maxDOB = dayjs().subtract(18, 'year').endOf('day');
      if (d.isAfter(maxDOB)) return true;
    }

    if (d.isBefore(dayjs().startOf('month')) && type !== 'dateOfBirth') return true;
    if (min && d.isBefore(dayjs(min))) return true;
    if (max && d.isAfter(dayjs(max))) return true;
    if (minYear && d.year() < minYear) return true;
    if (maxYear && d.year() > maxYear) return true;
    if (minMonth && d.month() < minMonth) return true;
    if (maxMonth && d.month() > maxMonth) return true;
    if (minDate && d.date() < minDate) return true;
    if (maxDate && d.date() > maxDate) return true;

    const day = d.date();
    // if (type === 'firstPayDate' && (day < 1 || day > 20)) return true;
    if (type === 'secondPayDate' && (day > d.daysInMonth())) return true;

    return false;
  };

  const handleDateClick = (date: any) => {
    const formattedOutput = dayjs(date).format('YYYY-MM-DD');
    setSelectedDate(dayjs(date));
    setCurrentMonth(dayjs(date));

    if (type === 'dateOfBirth') {
      setInputValue(formatDisplayForDOB(formattedOutput));
    } else {
      setInputValue(dayjs(date).format('MM/DD/YYYY'));
    }

    onChange(formattedOutput);

    if (type === 'firstPayDate' || type === 'secondPayDate') {
      const { valid, message } = validatePayDates();
      if (!valid) {
        setError(message || '');
        isErrorPresent?.(true);
        setShowCalendar(false);
        return;
      }
    }

    setError('');
    isErrorPresent?.(false);
    setShowCalendar(false);
  };

  const validateManualDate = (str: any) => {
    if (type === 'dateOfBirth' && str.includes('****')) return;

    const parsed = dayjs(str, 'MM/DD/YYYY', true);
    if (!parsed.isValid()) {
      if(str == '') {
        onChange('')
      }
      setError('Enter a valid date');
      isErrorPresent?.(true);
      return;
    }

    if (type === 'dateOfBirth') {
      const maxDOB = dayjs().subtract(18, 'year').endOf('day');
      if (parsed.isAfter(maxDOB)) {
        setError('You must be at least 18 years old. Future dates are not allowed.');
        isErrorPresent?.(true);
        return;
      }
    }

    if (type === 'firstPayDate' || type === 'secondPayDate') {
      const { valid, message } = validatePayDates();
      if (!valid) {
        setError(message || '');
        isErrorPresent?.(true);
        return;
      }
    }

    if (isDateDisabled(parsed)) {
      setError('Entered date is not valid');
      isErrorPresent?.(true);
      return;
    }

    setError('');
    isErrorPresent?.(false);
    setSelectedDate(parsed);
    setCurrentMonth(parsed);

    if (type === 'dateOfBirth') {
      setInputValue(formatDisplayForDOB(parsed.format('YYYY-MM-DD')));
    } else {
      setInputValue(parsed.format('MM/DD/YYYY'));
    }

    onChange(parsed.format('YYYY-MM-DD'));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value;
    validateManualDate(input.value);
    const selectionStart = input.selectionStart || 0;

    const { formatted, cursorPos } = formatWithCursor(rawValue, selectionStart);
    const parsed = dayjs(formatted, 'MM/DD/YYYY', true);
    if (type === 'dateOfBirth' && formatted.length === 10) {
      if (parsed.isValid()) {
        setInputValue(formatDisplayForDOB(parsed.format('YYYY-MM-DD')));
        onChange(parsed.format('YYYY-MM-DD'));
        setSelectedDate(parsed);
        setCurrentMonth(parsed);
        setError('');
        isErrorPresent?.(false);
        return;
      }
    }

    setInputValue(formatted);
    parsed.isValid() && input.value?.length == 10 ? onChange(parsed.format('YYYY-MM-DD')) : null;

    setTimeout(() => {
      if (input.setSelectionRange) {
        input.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);

    if (formatted.length === 10 && type !== 'dateOfBirth') {
      validateManualDate(formatted);
    } else {
      setError('');
      isErrorPresent?.(false);
    }
  };

  const formatWithCursor = (value: string, selectionStart: number) => {
    const digits = value.replace(/\D/g, '');
    let formatted = '';
    let cursorPos = selectionStart;

    for (let i = 0, digitIndex = 0; digitIndex < digits.length && i < 10; i++) {
      if (i === 2 || i === 5) {
        formatted += '/';
        if (i < cursorPos) cursorPos++;
      } else {
        formatted += digits[digitIndex];
        digitIndex++;
      }
    }

    return { formatted, cursorPos };
  };

  const renderDays = () => {
    const startOfMonth = currentMonth.startOf('month');
    const daysInMonth = currentMonth.daysInMonth();
    const startDay = startOfMonth.day();
    const days = [];

    for (let i = 0; i < startDay; i++) {
      days.push(<div className="day empty" key={`empty-${i}`} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const fullDate = currentMonth.date(day);
      const isDisabled = isDateDisabled(fullDate);
      const isSelected = selectedDate && fullDate.isSame(selectedDate, 'day');

      days.push(
        <div
          key={day}
          className={`day ${isDisabled ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
          onClick={() => !isDisabled && handleDateClick(fullDate)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const canGoPrevMonth = () => {
    const prevMonth = currentMonth.subtract(1, 'month');
    for (let day = 1; day <= prevMonth.daysInMonth(); day++) {
      if (!isDateDisabled(prevMonth.date(day))) return true;
    }
    return false;
  };

  const canGoNextMonth = () => {
    if(type != 'dateOfBirth') return true
    const nextMonth = currentMonth.add(1, 'month');
    for (let day = 1; day <= nextMonth.daysInMonth(); day++) {
      if (!isDateDisabled(nextMonth.date(day))) return true;
    }
    return false;
  };

  const prevMonth = () => {
    if (canGoPrevMonth()) {
      setCurrentMonth(currentMonth.subtract(1, 'month'));
    }
  };

  const nextMonth = () => {
    if (canGoNextMonth()) {
      setCurrentMonth(currentMonth.add(1, 'month'));
    }
  };

  return (
    <div className="datepicker-container" ref={containerRef}>
      <label className="datepicker-label">
        {label}
        {required && <span className="color-red">*</span>}
      </label>

      <div className="datepicker-input-wrapper">
        <input
          className="datepicker-input"
          placeholder={format.toLowerCase()}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={() => validateManualDate(inputValue)}
          disabled={disabled}
        />
        <span className="calendar-icon" onClick={() => setShowCalendar(!showCalendar)}>📅</span>
        {error && <div className="error-text">{error}</div>}

        {showCalendar && (
          <div className="calendar-popup">
            <div className="calendar-header">
              <button onClick={prevMonth} disabled={!canGoPrevMonth()}>&larr;</button>
              <span>{currentMonth.format('MMMM YYYY')}</span>
              <button onClick={nextMonth} disabled={!canGoNextMonth()}>&rarr;</button>
            </div>
            <div className="calendar-grid">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                <div className="day header" key={d}>{d}</div>
              ))}
              {renderDays()}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Open+Sans:wght@600&display=swap');

        .datepicker-container {
          font-family: 'Open Sans', sans-serif;
          display: flex;
          flex-direction: column;
          width: 100%;
          margin-top: 5px;
        }

        .datepicker-label {
          font-size: 14px;
          margin-bottom: 4px;
          text-align: left;
          font-family: 'OpenSans-semibold';
        }

        .datepicker-input-wrapper {
          position: relative;
        }

        .datepicker-input {
          width: 100%;
          padding: 10px 36px 10px 10px;
          border: 1px solid #ccc;
          border-radius: 6px;
          font-size: 16px;
          height: 30px;
        }

        .calendar-icon {
          position: absolute;
          right: 10px;
          top: 5px;
          cursor: pointer;
          font-size: 16px;
        }

        .calendar-popup {
          position: absolute;
          top: 100%;
          left: 0;
          margin-top: 6px;
          background: white;
          border: 1px solid #ccc;
          z-index: 1000;
          width: 260px;
          padding: 10px;
          border-radius: 6px;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
        }

        .calendar-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
          font-weight: bold;
        }

        .calendar-header button {
          background: none;
          border: none;
          font-size: 16px;
          cursor: pointer;
          font-weight: bold;
          color: #555;
        }

        .calendar-header button:disabled {
          color: #ccc;
          cursor: default;
        }

        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 4px;
        }

        .day {
          text-align: center;
          padding: 6px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
        }

        .day:hover {
          background: #f0f0f0;
        }

        .day.disabled {
          color: #ccc;
          pointer-events: none;
        }

        .day.selected {
          background: #1976d2;
          color: white;
        }

        .day.empty {
          pointer-events: none;
          visibility: hidden;
        }

        .day.header {
          font-weight: bold;
        }

        .error-text {
          color: red;
          font-size: 14px;
          margin-top: 4px;
          text-align: left;
        }

        .color-red {
          color: red;
          margin-left: 3px;
        }

        .datepicker-input:disabled {
          background-color: #EBEBE4;
          color: #aaa;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default CustomDatePicker;
